import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message, notification } from 'antd';
import { AxiosError } from 'axios';
import api from 'constants/api';
import { useEffect } from 'react';
import Api from 'util/Api';

export type AddressField =
  | 'postalCode'
  | 'streetNumber'
  | 'locality'
  | 'street';

export interface Address {
  lat?: number;
  lng?: number;
  normalizedAddress?: string;
  postalCode?: string;
  streetNumber?: string;
  street?: string;
  neighborhood?: string;
  subLocality?: string;
  locality?: string;
  county?: string;
  state?: string;
  country?: string;
  countryCode?: string;
  exactMatch?: boolean;
  zone?: {
    id?: number;
    name?: string;
    detail?: string;
  };
  forcedFields?: AddressField[];
}

export interface ShippingAddress {
  address: Address;
  package: {
    shippingAddressComment?: string; // ShippingAddress.comment
    comment?: string; // ShippingAddress.shippingAddress.comment
    description?: string;
    addressDetail?: string;
    zone?: {
      id?: number;
      name?: string;
      detail?: string;
    };
  };
}

// Types for address operations
export interface AddressResponse extends ShippingAddress {
  message?: string;
  details?: string;
}

export interface AddressHistoryResponse {
  history?: Address[];
  message?: string;
}

export interface AddressProposalRequest {
  street?: string;
  number?: string;
  postalCode?: string;
  city?: string;
  marketplaceId?: number;
}

interface AlternativesResponse {
  alternatives?: Address[];
  ownerId?: number;
  message?: string;
}

interface Alternative {
  address: Address;
}

// Query functions
const fetchShipmentAddress = async (
  shipmentId: number | string,
): Promise<ShippingAddress | undefined> => {
  try {
    const response = await Api.apiAxios.get<AddressResponse>(
      api.addresses.addressByShipmentId(shipmentId),
    );

    if (!Api.isSuccessResponse(response)) {
      throw new AxiosError(
        response.data.message,
        response.status.toString(),
        undefined,
        { shipmentId },
        response,
      );
    }

    return {
      ...response.data,
      address: {
        ...response.data.address,
        locality:
          response.data.address.locality ??
          response.data.address.county ??
          response.data.address.state ??
          '',
      },
    };
  } catch (error) {
    console.error(error);
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new Error('Ocurrió un error inesperado al obtener la dirección.');
  }
};

const recalculateAddress = async (
  shipmentId: number | string,
): Promise<ShippingAddress | undefined> => {
  try {
    const response = await Api.apiAxios.post<AddressResponse>(
      api.addresses.recalculate(shipmentId),
      { type: 'destination' },
    );

    if (!Api.isSuccessResponse(response)) {
      throw new AxiosError(
        response.data.message,
        response.status.toString(),
        undefined,
        { shipmentId },
        response,
      );
    }

    return {
      ...response.data,
      address: {
        ...response.data.address,
        locality:
          response.data.address.locality ??
          response.data.address.county ??
          response.data.address.state ??
          '',
      },
    };
  } catch (error) {
    console.error(error);
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new Error('Ocurrió un error inesperado al recalcular la dirección.');
  }
};

const locateAddress = async (
  marketplaceId: number,
  proposal: AddressProposalRequest,
): Promise<Address[]> => {
  try {
    const response = await Api.apiAxios.post<AlternativesResponse>(
      api.addresses.calculate,
      { ...proposal, marketplaceId },
    );

    if (
      !Api.isSuccessResponse(response) ||
      response.data.alternatives === undefined ||
      response.data.alternatives.length === 0
    ) {
      throw new AxiosError(
        response.data.message,
        response.status.toString(),
        undefined,
        { proposal },
        response,
      );
    }

    return response.data.alternatives.map((address) => ({
      ...address,
      locality: address.locality ?? address.county ?? address.state ?? '',
    })) as Address[];
  } catch (error) {
    console.error(error);
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new Error('Ocurrió un error inesperado al modificar la dirección.');
  }
};

const saveAddress = async (
  shipmentId: number | string,
  address: ShippingAddress,
): Promise<ShippingAddress> => {
  try {
    const response = await Api.apiAxios.put<AddressResponse>(
      api.addresses.addressByShipmentId(shipmentId),
      address,
    );

    if (!Api.isSuccessResponse(response)) {
      throw new AxiosError(
        response.data.message,
        response.status.toString(),
        undefined,
        { shipmentId },
        response,
      );
    }

    return response.data;
  } catch (error) {
    console.error(error);
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new Error('Ocurrió un error inesperado al guardar los cambios.');
  }
};

const fetchAddressAlternatives = async (
  shipmentId: number | string,
): Promise<AlternativesResponse> => {
  try {
    const response = await Api.apiAxios.get<AlternativesResponse>(
      api.addresses.alternativesByShipmentId(shipmentId),
    );

    if (!Api.isSuccessResponse(response)) {
      throw new AxiosError(
        (response.data as { message: string }).message,
        response.status.toString(),
        undefined,
        { shipmentId },
        response,
      );
    }

    const alternatives = (response.data.alternatives as Alternative[]).map(
      (alt) => ({
        ...alt.address,
        locality:
          alt.address.locality ?? alt.address.county ?? alt.address.state ?? '',
      }),
    ) as Address[];
    return response.data.ownerId
      ? { alternatives, ownerId: response.data.ownerId }
      : { alternatives };
  } catch (error) {
    console.error(error);
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new Error('Ocurrió un error inesperado al obtener las alternativas.');
  }
};

const fetchAddressHistory = async (
  shipmentId: number | string,
): Promise<Address[] | undefined> => {
  try {
    const response = await Api.apiAxios.get<AddressHistoryResponse>(
      api.addresses.historyByShipmentId(shipmentId),
    );

    if (!Api.isSuccessResponse(response)) {
      throw new AxiosError(
        response.data.message,
        response.status.toString(),
        undefined,
        { shipmentId },
        response,
      );
    }

    return response.data.history;
  } catch (error) {
    console.error(error);
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new Error(
      'Ocurrió un error inesperado al obtener el historial de direcciones.',
    );
  }
};

// Hooks
export const useShipmentAddress = (
  shipmentId: number | string,
  enabled = true,
) => {
  const queryClient = useQueryClient();

  const query = useQuery<ShippingAddress | undefined>({
    queryKey: ['shipmentAddress', shipmentId],
    queryFn: () => fetchShipmentAddress(shipmentId),
    retry: 1,
    enabled: enabled && !!shipmentId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (
      query.error &&
      query.error instanceof AxiosError &&
      query.error.response?.status === 404
    ) {
      const handleRecalculate = async () => {
        try {
          await recalculateAddress(shipmentId);

          // Invalidar caches para forzar refetch
          queryClient.invalidateQueries({
            queryKey: ['shipmentAddress', shipmentId],
          });
          queryClient.invalidateQueries({
            queryKey: ['addressAlternatives', shipmentId],
          });
          queryClient.invalidateQueries({
            queryKey: ['addressHistory', shipmentId],
          });
        } catch (recalculateError) {
          message.error('No se pudo encontrar la dirección.');
        }
      };

      handleRecalculate();
    }
  }, [query.error, shipmentId, queryClient]);

  return query;
};

export const useLocateAddress = (marketplaceId?: number) => {
  return useMutation({
    mutationFn: async (proposal: AddressProposalRequest) => {
      if (!marketplaceId) return null;
      return await locateAddress(marketplaceId, proposal);
    },
    onSuccess: (data) => {
      if (data && data.length > 0) {
        if (data.length > 1) {
          message.success(
            'Dirección localizada. Se encontraron varias alternativas.',
          );
        } else {
          message.success('Dirección encontrada.');
        }
      }
    },
    onError: (error: AxiosError<any>) => {
      console.error(error);
      const statusCode = error.response?.status;

      if (statusCode === 404) {
        return message.info('No se encontró el envío.');
      }
      if (statusCode === 500) {
        return message.error('Ocurrió un error en el servidor.');
      }

      notification.error({
        message: 'Error al buscar la dirección',
        description: error.message,
      });
    },
  });
};

export const useSaveAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      shipmentId,
      shippingAddress,
    }: {
      shipmentId: number | string;
      shippingAddress: ShippingAddress;
    }) => saveAddress(shipmentId, shippingAddress),
    onSuccess: (data, { shipmentId }) => {
      queryClient.invalidateQueries({
        queryKey: ['shipmentAddress', shipmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ['addressAlternatives', shipmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ['addressHistory', shipmentId],
      });
      message.success('Dirección guardada correctamente.');
      if (!data.package.zone || !data.package.zone.id) {
        notification.warning({
          message: `Dirección sin zonificar`,
          description: `No hay ninguna zona definida para esta dirección.`,
        });
      }
    },
    onError: (error: AxiosError<any>) => {
      console.error(error);
      const statusCode = error.response?.status;
      const code = error.response?.data?.code;

      if (statusCode === 404) {
        return message.info('No se encontró el envío.');
      }
      if (statusCode === 409) {
        return message.error('No se pudo guardar los cambios.');
      }
      if (statusCode === 500) {
        return message.error('Ocurrió un error en el servidor.');
      }

      notification.error({
        message: 'Error al guardar la dirección',
        description: error.message,
      });
    },
  });
};

export const useAddressAlternatives = (
  shipmentId: number | string,
  enabled = true,
) => {
  return useQuery<AlternativesResponse>({
    queryKey: ['addressAlternatives', shipmentId],
    queryFn: () => fetchAddressAlternatives(shipmentId),
    retry: 1,
    enabled: enabled && !!shipmentId,
  });
};

export const useAddressHistory = (
  shipmentId: number | string,
  enabled = true,
) => {
  return useQuery<Address[] | undefined>({
    queryKey: ['addressHistory', shipmentId],
    queryFn: () => fetchAddressHistory(shipmentId),
    retry: 1,
    enabled: enabled && !!shipmentId,
  });
};
